import type { SVGProps } from 'react';

/**
 * Hand-rolled line icons — 24x24, stroke-based, `currentColor`.
 *
 * Kept in-house rather than pulling an icon package: the set needed here is
 * small and fixed (nav, login, a handful of stat tiles), and a dependency for
 * a dozen glyphs is a worse trade than fifty lines of SVG that never drift out
 * of sync with the design tokens.
 */

export type IconProps = SVGProps<SVGSVGElement>;

const base = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export function IconLayoutGrid(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <rect x="3.5" y="3.5" width="7.5" height="7.5" rx="1.8" />
      <rect x="13" y="3.5" width="7.5" height="7.5" rx="1.8" />
      <rect x="3.5" y="13" width="7.5" height="7.5" rx="1.8" />
      <rect x="13" y="13" width="7.5" height="7.5" rx="1.8" />
    </svg>
  );
}

export function IconContact(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="9" cy="8" r="3.25" />
      <path d="M3.75 19.5c0-3.1 2.35-5.25 5.25-5.25s5.25 2.15 5.25 5.25" />
      <path d="M15.5 5.5c1.6.35 2.75 1.8 2.75 3.5s-1.15 3.15-2.75 3.5" />
      <path d="M19 14.7c1.6.4 2.75 1.85 2.75 3.55" />
    </svg>
  );
}

export function IconUsers(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="8.5" cy="7.5" r="3.25" />
      <circle cx="17" cy="8.5" r="2.5" />
      <path d="M2.75 19.5c0-3.05 2.55-5.25 5.75-5.25s5.75 2.2 5.75 5.25" />
      <path d="M15.5 14.9c2.4.35 4 2.15 4 4.6" />
    </svg>
  );
}

export function IconShieldCheck(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M12 3.25 19 6v5.8c0 4.5-3 7.4-7 9.4-4-2-7-4.9-7-9.4V6z" />
      <path d="m9 12 2.1 2.1L15.5 9.6" />
    </svg>
  );
}

export function IconSliders(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M4 6.5h9.5M17 6.5h2.5M4 12h5.5M13 12h6.5M4 17.5h11.5M19.5 17.5H22" />
      <circle cx="15.5" cy="6.5" r="1.9" />
      <circle cx="11.5" cy="12" r="1.9" />
      <circle cx="18" cy="17.5" r="1.9" />
    </svg>
  );
}

export function IconLogOut(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M9 20H5.75A1.75 1.75 0 0 1 4 18.25V5.75A1.75 1.75 0 0 1 5.75 4H9" />
      <path d="M15.5 16.5 20 12l-4.5-4.5" />
      <path d="M20 12H9.5" />
    </svg>
  );
}

export function IconMenu(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M3.5 6.5h17M3.5 12h17M3.5 17.5h17" />
    </svg>
  );
}

export function IconClose(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M5.5 5.5l13 13M18.5 5.5l-13 13" />
    </svg>
  );
}

export function IconMail(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <rect x="3" y="5.5" width="18" height="13" rx="2.25" />
      <path d="m3.75 6.75 7.6 6.15a1 1 0 0 0 1.3 0l7.6-6.15" />
    </svg>
  );
}

export function IconLock(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <rect x="4.75" y="10.5" width="14.5" height="9.5" rx="2.2" />
      <path d="M8 10.5V7.75a4 4 0 0 1 8 0V10.5" />
    </svg>
  );
}

export function IconArrowRight(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M4 12h16M13 5l7 7-7 7" />
    </svg>
  );
}

export function IconTrendUp(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="m3.5 16.5 6-6.2 4 4L21 6.5" />
      <path d="M15 6.5h6v6" />
    </svg>
  );
}

export function IconAlertTriangle(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M10.5 4.15 2.9 18a1.7 1.7 0 0 0 1.5 2.5h15.2a1.7 1.7 0 0 0 1.5-2.5L13.5 4.15a1.7 1.7 0 0 0-3 0Z" />
      <path d="M12 10v4.2" />
      <circle cx="12" cy="17.4" r="0.15" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function IconSpark(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M12 3.5c.5 3.6 2.4 5.5 6 6-3.6.5-5.5 2.4-6 6-.5-3.6-2.4-5.5-6-6 3.6-.5 5.5-2.4 6-6Z" />
    </svg>
  );
}

export function IconPhoneOff(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M4 4.5 20 20" />
      <path d="M8.2 8.7c-.85.85-1.2 1.75-.6 3.15.9 2.1 2.55 4 4.7 5.35 1.15.7 2.1.5 2.85-.15l1.15-1" />
      <path d="M14.9 5.35c1.75.35 3.3 1.15 4.55 2.35.65.65.85 1.55.35 2.5l-.9 1.5" />
    </svg>
  );
}

export function IconUserX(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="9" cy="8" r="3.25" />
      <path d="M3.75 19.5c0-3.1 2.35-5.25 5.25-5.25s5.25 2.15 5.25 5.25" />
      <path d="m16.5 8 4 4m0-4-4 4" />
    </svg>
  );
}

export function IconAward(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="8.5" r="4.75" />
      <path d="m8.3 12.4-1.55 7.35L12 17.2l5.25 2.55-1.55-7.35" />
    </svg>
  );
}

export function IconCopy(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <rect x="8.75" y="8.75" width="11.5" height="11.5" rx="2" />
      <path d="M15.25 8.75V5.75A2 2 0 0 0 13.25 3.75H5.75a2 2 0 0 0-2 2v7.5a2 2 0 0 0 2 2h3" />
    </svg>
  );
}

export function IconCalendar(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2.2" />
      <path d="M3.5 9.75h17M8 3.25v3M16 3.25v3" />
    </svg>
  );
}

export function IconChevronDown(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function IconRoute(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="5.5" cy="6" r="2.25" />
      <circle cx="18.5" cy="18" r="2.25" />
      <path d="M5.5 8.25V14a3 3 0 0 0 3 3h5.5a3 3 0 0 0 3-3v-.5" />
      <path d="m14 10 3-3 3 3" />
    </svg>
  );
}

export function IconShuffle(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M3.5 6.5h3.2c1.4 0 2.2.6 3 1.9l4.6 7.2c.8 1.3 1.6 1.9 3 1.9h3.2" />
      <path d="m17.5 4.5 3 2.5-3 2.5" />
      <path d="M3.5 17.5h3.2c1.4 0 2.2-.6 3-1.9l.7-1.1" />
      <path d="m17.5 19.5 3-2.5-3-2.5" />
      <path d="M13.7 8.5 14.4 7.4c.8-1.3 1.6-1.9 3-1.9h3.2" />
    </svg>
  );
}

export function IconSearch(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="10.75" cy="10.75" r="6.25" />
      <path d="m19.25 19.25-4-4" />
    </svg>
  );
}

/**
 * Teams: one figure above two, i.e. a manager over their counsellors. Distinct
 * enough from IconUsers (a flat group) that the two nav entries don't read as
 * the same screen.
 */
export function IconTeams(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="5.25" r="2.75" />
      <path d="M12 8v3.5M6.5 15.5v-1.75a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v1.75" />
      <circle cx="6.5" cy="18" r="2.5" />
      <circle cx="17.5" cy="18" r="2.5" />
    </svg>
  );
}

export function IconMegaphone(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M4 9.5v4a1.5 1.5 0 0 0 1.5 1.5H8l6.5 4V5.5L8 9.5H5.5A1.5 1.5 0 0 0 4 11z" />
      <path d="M8 15v3.5a1.5 1.5 0 0 0 3 0V17" />
      <path d="M18 9.5a3.5 3.5 0 0 1 0 5" />
    </svg>
  );
}

export function IconKey(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="8" cy="15.5" r="3.5" />
      <path d="M10.6 13 19 4.5" />
      <path d="M16.5 7l2.2 2.2" />
      <path d="M14 9.5l2.2 2.2" />
    </svg>
  );
}

export function IconMessage(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

export function IconSun(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="12" r="5" />
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    </svg>
  );
}

export function IconMoon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

export function IconEye(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export function IconEyeOff(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  );
}
